/**
 * HeaderActionButtons Component
 *
 * Renders the action buttons for company details header.
 * Includes website, linkedin, search, watchlist, and download buttons
 * with responsive tooltip positioning and download popup.
 */

import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import DataSourceToggleIcon from '../../../common/DataSourceToggleIcon';
import HeaderDownloadPopup from './HeaderDownloadPopup';
import { headerActionPropTypes } from './headerActionPropTypes';

/**
 * @param {Object} props
 * @param {Object} props.company - Company data with id, name, website, website_SAFE
 * @param {boolean} props.isToggleFinancialDownload - Whether download popup is toggled open
 * @param {string} props.linkedInTooltipText - Tooltip text for LinkedIn button
 * @param {string} props.searchTooltipText - Tooltip text for search button
 * @param {boolean} props.isSearchDisabled - Whether search button is disabled
 * @param {React.ReactNode} props.watchlistTooltipContent - Tooltip content for watchlist button
 * @param {string} props.watchlistTooltipPosition - Tooltip position for watchlist button
 * @param {boolean} props.watchlistTooltipClickable - Whether watchlist tooltip is clickable
 * @param {boolean} props.isWatchlistDisabled - Whether watchlist button is disabled
 * @param {boolean} props.isInWatchlist - Whether company is in watchlist
 * @param {React.ReactNode} props.downloadTooltipContent - Tooltip content for download button
 * @param {number} props.windowWidth - Current window width for responsive tooltips
 * @param {Object} props.downloadPopupProps - Props to pass to HeaderDownloadPopup
 * @param {Function} props.onWebsiteClick - Handler for website button click
 * @param {Function} props.onLinkedInClick - Handler for LinkedIn button click
 * @param {Function} props.onSearchClick - Handler for search button click
 * @param {Function} props.onWatchlistClick - Handler for watchlist button click
 * @param {Function} props.onDownloadToggle - Handler for download toggle button click
 * @param {Function} props.onDownload - Handler for download action from popup
 * @param {Function} props.requestBulkExport - Handler for bulk-gated download action (comparables)
 */
const HeaderActionButtons = ({
  company,
  isToggleFinancialDownload,
  linkedInTooltipText,
  searchTooltipText,
  isSearchDisabled,
  watchlistTooltipContent,
  watchlistTooltipPosition,
  watchlistTooltipClickable,
  isWatchlistDisabled,
  isInWatchlist,
  downloadTooltipContent,
  windowWidth,
  downloadPopupProps,
  onWebsiteClick,
  onLinkedInClick,
  onSearchClick,
  onWatchlistClick,
  onDownloadToggle,
  onDownload,
  requestBulkExport
}) => {
  const websiteTooltipPosition = windowWidth <= 500 ? 'bottom-start' : 'bottom-center';
  const downloadButtonTooltipPosition = windowWidth < 1200 ? 'bottom-end' : undefined;

  return (
    <div className="action-btns">
      {!!company.website && (
        <IconButton
          id="website-btn"
          type={ICON_BUTTON_TYPES.BLUE}
          icon={ICON_BUTTON_ICONS.GLOBE2}
          tooltipText={isToggleFinancialDownload ? null : `Visit ${company.website}`}
          onClick={onWebsiteClick}
          tooltipPosition={websiteTooltipPosition}
        />
      )}
      <IconButton
        id="linkedin-btn"
        type={ICON_BUTTON_TYPES.BLUE}
        icon={ICON_BUTTON_ICONS.LINKEDIN}
        tooltipText={isToggleFinancialDownload ? null : linkedInTooltipText}
        onClick={onLinkedInClick}
      />
      <IconButton
        id="search-from-company-details-btn"
        type={ICON_BUTTON_TYPES.BLUE}
        icon={ICON_BUTTON_ICONS.SEARCH}
        onClick={onSearchClick}
        tooltipText={isToggleFinancialDownload ? null : searchTooltipText}
        disabled={isSearchDisabled}
        {...(windowWidth > 1200 && windowWidth < 1500 ? { tooltipPosition: 'top-end' } : {})}
      />
      <IconButton
        id="psrd-btn"
        type={ICON_BUTTON_TYPES.BLUE}
        icon={ICON_BUTTON_ICONS.WATCHLIST}
        disabled={isWatchlistDisabled}
        tooltipContent={isToggleFinancialDownload ? null : watchlistTooltipContent}
        tooltipPosition={watchlistTooltipPosition}
        tooltipClickable={watchlistTooltipClickable}
        filled={isInWatchlist}
        onClick={onWatchlistClick}
      />
      <DataSourceToggleIcon id="data-sources-btn-mobile" />
      <IconButton
        id="psrf-btn"
        type={ICON_BUTTON_TYPES.BLUE}
        icon={ICON_BUTTON_ICONS.DOWNLOAD}
        onClick={onDownloadToggle}
        filled={isToggleFinancialDownload}
        tooltipContent={isToggleFinancialDownload ? null : downloadTooltipContent}
        {...(downloadButtonTooltipPosition
          ? { tooltipPosition: downloadButtonTooltipPosition }
          : {})}
      />
      {isToggleFinancialDownload && (
        <HeaderDownloadPopup
          onDownload={onDownload}
          requestBulkExport={requestBulkExport}
          {...downloadPopupProps}
        />
      )}
    </div>
  );
};

HeaderActionButtons.propTypes = headerActionPropTypes;

export default HeaderActionButtons;
